import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'
import Image from 'next/image'
import services from '../services'
import { APIURL } from '../../constants'
import PropTypes from 'prop-types'

// ViewBox with generous padding: triangle stays inside; labels sit in the dark margin (not on the fill).
const VB = { x: 0, y: 0, width: 760, height: 608 }
const PAD = { left: 56, right: 56, top: 44, bottom: 62 }
const usableW = VB.width - PAD.left - PAD.right
const usableH = VB.height - PAD.top - PAD.bottom
const cx = PAD.left + usableW / 2
const baseY = VB.height - PAD.bottom
const side = Math.max(
  0,
  Math.min(usableW, (usableH * 2) / Math.sqrt(3))
)
const alt = side * Math.sqrt(3) / 2
const topY = baseY - alt
const A = { x: cx, y: topY }
const B = { x: cx - side / 2, y: baseY }
const C = { x: cx + side / 2, y: baseY }
const centroid = { x: (A.x + B.x + C.x) / 3, y: (A.y + B.y + C.y) / 3 }
// Labels sit in the padded margin (dark background), not on the triangle fill.
const LABEL_PERF = { x: A.x, y: Math.max(14, A.y - 22) }
const LABEL_PRICE_DESKTOP = { x: B.x + 50, y: Math.min(VB.height - 10, baseY + 22) }
// Mobile: nudge "Affordable" (Size shares y) — see `labelPrice` in the component.
const LABEL_PRICE_MOBILE = { x: B.x + 80, y: Math.min(VB.height - 10, baseY + 40) }
// Makes exact corner intents easier: a few "ticks" inside a corner still snaps to pure 100-0-0.
const CORNER_SNAP_RADIUS = 16

function sub (u, v) { return { x: u.x - v.x, y: u.y - v.y } }
function add (u, v) { return { x: u.x + v.x, y: u.y + v.y } }
function dotp (u, v) { return u.x * v.x + u.y * v.y }
function cross (u, v) { return u.x * v.y - u.y * v.x }
function len (u) { return Math.hypot(u.x, u.y) }
function inside (p, a, b, c) {
  const v0 = sub(c, a); const v1 = sub(b, a); const v2 = sub(p, a)
  const d00 = dotp(v0, v0); const d01 = dotp(v0, v1); const d02 = dotp(v0, v2)
  const d11 = dotp(v1, v1); const d12 = dotp(v1, v2)
  const inv = 1 / (d00 * d11 - d01 * d01)
  const uu = (d11 * d02 - d01 * d12) * inv
  const vv = (d00 * d12 - d01 * d02) * inv
  return uu >= 0 && vv >= 0 && (uu + vv) <= 1
}
function inter (p1, p2, q1, q2) {
  const r = sub(p2, p1); const s = sub(q2, q1); const rxs = cross(r, s)
  if (Math.abs(rxs) < 1e-9) return null
  const t = cross(sub(q1, p1), s) / rxs
  const u = cross(sub(q1, p1), r) / rxs
  if (t >= 0 && t <= 1 && u >= 0 && u <= 1) return add(p1, { x: r.x * t, y: r.y * t })
  return null
}
function clampToTri (p) {
  if (inside(p, A, B, C)) return p
  const xs = [inter(centroid, p, A, B), inter(centroid, p, B, C), inter(centroid, p, C, A)].filter(Boolean)
  return xs[0] || centroid
}
function snapToCornerIfNear (p) {
  if (len(sub(p, A)) <= CORNER_SNAP_RADIUS) return { ...A }
  if (len(sub(p, B)) <= CORNER_SNAP_RADIUS) return { ...B }
  if (len(sub(p, C)) <= CORNER_SNAP_RADIUS) return { ...C }
  return p
}
/**
 * Barycentric weights: at vertex B → 100% Price; C → 100% Size; A → 100% Performance.
 * On an edge, the two adjacent corners split linearly (e.g. midpoint BC = 50% Size, 50% Price).
 */
function weightsFromPoint (p) {
  const v0 = sub(C, A)
  const v1 = sub(B, A)
  const v2 = sub(p, A)
  const d00 = dotp(v0, v0)
  const d01 = dotp(v0, v1)
  const d11 = dotp(v1, v1)
  const d02 = dotp(v0, v2)
  const d12 = dotp(v1, v2)
  const denom = d00 * d11 - d01 * d01
  if (Math.abs(denom) < 1e-12) {
    return { price: 1 / 3, size: 1 / 3, perf: 1 / 3 }
  }
  const uu = (d11 * d02 - d01 * d12) / denom
  const vv = (d00 * d12 - d01 * d02) / denom
  const wPerf = 1 - uu - vv
  const wPrice = vv
  const wSize = uu
  const s = wPrice + wSize + wPerf
  if (s < 1e-12) return { price: 1 / 3, size: 1 / 3, perf: 1 / 3 }
  return { price: wPrice / s, size: wSize / s, perf: wPerf / s }
}

/** Prices above this share the same (worst) affordability score on the linear scale; avoids $12k skewing the whole axis. */
const PRICE_SCORE_CAP_DOLLARS = 5000

/** Floor for the size axis when the API has no small sets (e.g. only 85"+); avoids 85" scoring 0 vs 100". */
const TV_SIZE_NORM_FLOOR_INCHES = 43
/** Diminishing-returns size curve: <1 compresses top-end advantage (100" no longer runs away as quickly). */
const SIZE_SCORE_POWER_EXPONENT = 0.2
/** Card-size meter scale: 42" should read empty, 100" should read full. */
const SIZE_METER_MIN_INCHES = 42
const SIZE_METER_MAX_INCHES = 100
/** Card-performance meter scale: 50 should read empty, 100 should read full. */
const PERFORMANCE_METER_MIN = 50
const PERFORMANCE_METER_MAX = 100
/** Upper bound for scaling Match /100 UI (max weighted 0–300 + synergy + oversize headroom). */
const MATCH_SCORE_DISPLAY_BONUS_HEADROOM = 22

const TV_RESULTS_LAYOUT_EASE = [0.22, 1, 0.36, 1]
const TV_RESULTS_EXIT_EASE = [0.4, 0, 0.2, 1]
const OPTIMIZE_OPTIONS = [
  { value: 'mixed_use', label: 'Mixed Use', field: 'mixed_use_score' },
  { value: 'home_theater', label: 'Home Theater', field: 'movies_score' },
  { value: 'sports', label: 'Sports', field: 'sports_score' },
  { value: 'gaming', label: 'Gaming', field: 'video_games_score' }
]

function deviceTypeFromViewport () {
  if (typeof window === 'undefined') return 'unknown'
  return window.matchMedia('(max-width: 980px)').matches ? 'mobile' : 'desktop'
}

function buyAngleSourceLabel () {
  if (typeof window === 'undefined') return 'buy-angle'
  const p = (window.location && window.location.pathname) || ''
  return p ? 'buy-angle:' + p : 'buy-angle'
}

/**
 * POST BuyAngle click telemetry with keepalive so the browser can finish the request
 * after the user follows a link (new tab / navigation). Matches base URL + JWT header from services.post_v2.
 */
function postBuyAngleClickKeepalive (body) {
  if (typeof window === 'undefined') return Promise.resolve(null)
  const baseRaw = typeof process !== 'undefined' && process.env.NEXT_PUBLIC_BACKEND_HOST
    ? String(process.env.NEXT_PUBLIC_BACKEND_HOST)
    : ''
  const base = baseRaw.replace(/\/$/, '')
  const path = APIURL.BUY_ANGLE_CLICK.replace(/^\//, '')
  const url = base ? `${base}/${path}` : `/${path}`
  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json'
  }
  try {
    const token = window.localStorage && window.localStorage.token
    if (token) headers.Authorization = 'JWT ' + token
  } catch (_) {}
  const payload = { ...body }
  if (payload.buy_angle_data_id == null) delete payload.buy_angle_data_id
  if (payload.session_uuid == null) delete payload.session_uuid
  return fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
    keepalive: true,
    credentials: 'omit'
  }).then((r) => r.json().catch(() => null))
}

const BUY_ANGLE_FINGERPRINT_STORAGE_KEY = 'user_fingerprint'
let buyAngleFpPromise = null

function getBuyAngleFingerprint () {
  if (typeof window === 'undefined') return Promise.resolve(null)
  try {
    const cached = window.localStorage && window.localStorage.getItem(BUY_ANGLE_FINGERPRINT_STORAGE_KEY)
    if (cached) return Promise.resolve(cached)
  } catch (_) {}
  if (!buyAngleFpPromise) {
    buyAngleFpPromise = import('@fingerprintjs/fingerprintjs')
      .then((mod) => mod.load())
      .then((fp) => fp.get())
      .then((res) => {
        const id = res && res.visitorId ? res.visitorId : null
        if (id) {
          try {
            if (window.localStorage) window.localStorage.setItem(BUY_ANGLE_FINGERPRINT_STORAGE_KEY, id)
          } catch (_) {}
        }
        return id
      })
      .catch(() => null)
  }
  return buyAngleFpPromise
}

/** Linear map to 0–100 (higher raw → higher score). */
function normalize100 (v, min, max) {
  if (max === min) return 0
  return ((v - min) / (max - min)) * 100
}

/** Linear map to 0–100 (lower raw → higher score), e.g. price. */
function inverseNormalize100 (v, min, max) {
  if (max === min) return 0
  return ((max - v) / (max - min)) * 100
}

/** Best TV in the list = 100; others scale by the same ratio vs that peak. */
function normalizePerformanceAgainstLeader (v, maxPerf) {
  if (maxPerf == null || maxPerf <= 0) return 0
  return Math.min(100, (v / maxPerf) * 100)
}

function normalizeSizePower100 (v, min, max) {
  if (max === min) return 0
  const linear = Math.min(1, Math.max(0, (v - min) / (max - min)))
  return (linear ** SIZE_SCORE_POWER_EXPONENT) * 100
}

function fixedSizeMeterPct (sizeInches) {
  const raw = ((sizeInches - SIZE_METER_MIN_INCHES) / (SIZE_METER_MAX_INCHES - SIZE_METER_MIN_INCHES)) * 100
  return Math.round(Math.min(100, Math.max(0, raw)))
}

function fixedPerformanceMeterPct (performanceScore) {
  const raw = ((performanceScore - PERFORMANCE_METER_MIN) / (PERFORMANCE_METER_MAX - PERFORMANCE_METER_MIN)) * 100
  return Math.round(Math.min(100, Math.max(0, raw)))
}

function performanceRatingFor (tv, optimizeFor) {
  const option = OPTIMIZE_OPTIONS.find(o => o.value === optimizeFor) || OPTIMIZE_OPTIONS[0]
  const raw = tv[option.field] || 0
  return raw * 10
}

function metricScoresForTv (tv, ctx) {
  const {
    priceNormMin,
    priceNormMax,
    sizeNormMin,
    sizeNormMax,
    maxPerformanceRating,
    optimizeFor
  } = ctx
  const performanceRating = performanceRatingFor(tv, optimizeFor)
  const priceForNorm = Math.min(tv.exact_price || 0, priceNormMax)
  const sizeInchesForNorm = Math.min(tv.size || 0, sizeNormMax)
  return {
    normalizedPriceScore: inverseNormalize100(priceForNorm, priceNormMin, priceNormMax),
    normalizedSizeScore: normalizeSizePower100(sizeInchesForNorm, sizeNormMin, sizeNormMax),
    normalizedPerformanceScore: normalizePerformanceAgainstLeader(
      performanceRating,
      maxPerformanceRating
    )
  }
}

/**
 * Triangle TV ranking: user “scores” 0–100 from the UI meters → weights 0–1;
 * each TV axis normalized 0–100; weighted sum + synergy / oversize bonuses.
 */
function triangleTvFinalScore (tv, ctx) {
  const {
    userPriceWeight,
    userSizeWeight,
    userPerformanceWeight
  } = ctx

  const {
    normalizedPriceScore,
    normalizedSizeScore,
    normalizedPerformanceScore
  } = metricScoresForTv(tv, ctx)

  const weightedScore =
    normalizedPriceScore * userPriceWeight +
    normalizedSizeScore * userSizeWeight +
    normalizedPerformanceScore * userPerformanceWeight

  const ranked = [
    { key: 'price', w: userPriceWeight },
    { key: 'size', w: userSizeWeight },
    { key: 'perf', w: userPerformanceWeight }
  ].sort((a, b) => b.w - a.w)
  const topTwo = new Set([ranked[0].key, ranked[1].key])
  const inTopTwo = (a, b) => topTwo.has(a) && topTwo.has(b)

  let bonus = 0
  if (inTopTwo('price', 'size') && normalizedPriceScore > 70 && normalizedSizeScore > 70) bonus += 8
  if (inTopTwo('price', 'perf') && normalizedPriceScore > 70 && normalizedPerformanceScore > 70) bonus += 8
  if (inTopTwo('size', 'perf') && normalizedSizeScore > 70 && normalizedPerformanceScore > 70) bonus += 8

  return weightedScore + bonus
}

/**
 * PAAPI / API keys for filtering the list when a badge is clicked.
 * value is string (including '8K') except very_bright (boolean true) and size (number, inches).
 */
function tvSizeInchesForPill (tv) {
  if (tv == null || tv.size == null || tv.size === '') return null
  const n = Number(tv.size)
  return Number.isFinite(n) ? Math.round(n) : null
}

function tvMatchesPillFilter (tv, filter) {
  if (!filter) return true
  if (filter.key === 'brand') return (tv.brand || '') === filter.value
  if (filter.key === 'pixel_type') return (tv.pixel_type || '') === filter.value
  if (filter.key === 'resolution') return tv.resolution === filter.value
  if (filter.key === 'very_bright') return tv.room_brightness_text === 'very_bright'
  if (filter.key === 'size') {
    const a = tvSizeInchesForPill(tv)
    const b = typeof filter.value === 'number' ? filter.value : Math.round(Number(filter.value))
    if (a == null || !Number.isFinite(b)) return false
    return a === b
  }
  return true
}

/** TV must satisfy every filter (AND). */
function tvMatchesAllPillFilters (tv, filters) {
  if (!filters || !filters.length) return true
  return filters.every((f) => tvMatchesPillFilter(tv, f))
}

function samePillFilter (a, b) {
  if (!a && !b) return true
  if (!a || !b) return false
  return a.key === b.key && a.value === b.value
}

function pillIsActiveInList (filters, def) {
  if (!filters || !filters.length) return false
  return filters.some((f) => samePillFilter(f, def))
}

function formatPillFilterOne (filter) {
  if (filter.key === 'brand') return 'Brand: ' + filter.value
  if (filter.key === 'pixel_type') return 'Type: ' + filter.value
  if (filter.key === 'resolution') return filter.value
  if (filter.key === 'very_bright') return 'Bright Room Approved'
  if (filter.key === 'size') return 'Size: ' + filter.value + '"'
  return ''
}

function filterBarPillClassName () {
  return 'tv-triangle-pill tv-triangle-pill--active'
}

function filterPillReactKey (f) {
  return f.key + ':' + String(f.value)
}

export default function TvTrianglePicker ({ onReady }) {
  const [tvs, setTvs] = useState([])
  const [loading, setLoading] = useState(true)
  const [initError, setInitError] = useState(false)
  const [pillFilters, setPillFilters] = useState([])
  const [handlePoint, setHandlePoint] = useState({ ...centroid })
  const [dragging, setDragging] = useState(false)
  const [optimizeFor, setOptimizeFor] = useState('mixed_use')
  const svgRef = useRef(null)
  const stackRef = useRef(null)
  const handleRef = useRef(null)
  const buyAngleFingerprintRef = useRef(null)
  const sessionUuidRef = useRef(null)
  const buyAngleDataIdRef = useRef(null)
  /** Only one BUY_ANGLE_DATA payload needs clicked_filters: true; server ORs it into BuyAngleData. */
  const buyAngleClickedFiltersSentRef = useRef(false)
  /** Synchronous — document touchmove must see “dragging” before the next frame or scroll wins (mobile). */
  const draggingRef = useRef(false)
  const reduceMotion = useReducedMotion()

  const [labelPrice, setLabelPrice] = useState(LABEL_PRICE_DESKTOP)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia('(max-width: 980px)')
    const apply = () => setLabelPrice(mq.matches ? LABEL_PRICE_MOBILE : LABEL_PRICE_DESKTOP)
    apply()
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', apply)
      return () => mq.removeEventListener('change', apply)
    }
    mq.addListener(apply)
    return () => mq.removeListener(apply)
  }, [])

  const labelSize = useMemo(
    () => ({ x: C.x - 10, y: labelPrice.y }),
    [labelPrice]
  )

  const filteredTvs = useMemo(() => {
    if (!pillFilters.length) return tvs
    return tvs.filter((tv) => tvMatchesAllPillFilters(tv, pillFilters))
  }, [tvs, pillFilters])

  const trackBuyAngleData = useCallback((payload) => {
    if (!sessionUuidRef.current) return
    const body = {
      ...payload,
      session_uuid: sessionUuidRef.current,
      buy_angle_data_id: buyAngleDataIdRef.current || undefined
    }
    if (buyAngleFingerprintRef.current) {
      body.user_fingerprint = buyAngleFingerprintRef.current
    }
    services.post_v2(APIURL.BUY_ANGLE_DATA, body).then((res) => {
      const id = res && res.response && res.response.buy_angle_data_id
      if (id) buyAngleDataIdRef.current = id
    }).catch(() => {})
  }, [])

  const trackBuyAngleClick = useCallback((payload) => {
    if (!sessionUuidRef.current) return
    const body = {
      ...payload,
      session_uuid: sessionUuidRef.current,
      buy_angle_data_id: buyAngleDataIdRef.current || undefined
    }
    if (buyAngleFingerprintRef.current) {
      body.user_fingerprint = buyAngleFingerprintRef.current
    }
    postBuyAngleClickKeepalive(body).then((res) => {
      const id = res && res.response && res.response.buy_angle_data_id
      if (id) buyAngleDataIdRef.current = id
    }).catch(() => {})
  }, [])

  const trackBuyAngleDataFiltersUsed = useCallback(() => {
    if (buyAngleClickedFiltersSentRef.current) return
    buyAngleClickedFiltersSentRef.current = true
    trackBuyAngleData({ clicked_filters: true })
  }, [trackBuyAngleData])

  const removePillFromBar = useCallback((f) => {
    trackBuyAngleDataFiltersUsed()
    setPillFilters((prev) => prev.filter((x) => !samePillFilter(x, f)))
  }, [trackBuyAngleDataFiltersUsed])

  const onPillClick = useCallback((e, nextFilter) => {
    e.preventDefault()
    e.stopPropagation()
    trackBuyAngleDataFiltersUsed()
    setPillFilters((prev) => {
      const exactIdx = prev.findIndex((f) => samePillFilter(f, nextFilter))
      if (exactIdx >= 0) {
        return prev.filter((_, i) => i !== exactIdx)
      }
      const keyIdx = prev.findIndex((f) => f.key === nextFilter.key)
      if (keyIdx >= 0) {
        return prev.map((f, i) => (i === keyIdx ? nextFilter : f))
      }
      return [...prev, nextFilter]
    })
  }, [trackBuyAngleDataFiltersUsed])

  useEffect(() => {
    let cancelled = false
    setInitError(false)
    async function run () {
      try {
        const fingerprint = await getBuyAngleFingerprint()
        if (cancelled) return
        buyAngleFingerprintRef.current = fingerprint
        const initPayload = {
          source: buyAngleSourceLabel(),
          device_type: deviceTypeFromViewport()
        }
        if (fingerprint) initPayload.user_fingerprint = fingerprint
        const [tvRes, initRes] = await Promise.all([
          services.get_v2(APIURL.TV_TRIANGLE_LIST),
          services.post_v2(APIURL.BUY_ANGLE_INIT, initPayload)
        ])
        if (cancelled) return
        const rp = initRes && initRes.response
        if (!initRes || initRes.status !== 'success' || !rp || !rp.session_uuid) {
          setInitError(true)
          setLoading(false)
          if (onReady) onReady()
          return
        }
        sessionUuidRef.current = rp.session_uuid
        buyAngleDataIdRef.current = rp.buy_angle_data_id
        if (tvRes && tvRes.response && tvRes.response.tvs) {
          setTvs(tvRes.response.tvs)
        }
        setLoading(false)
        if (onReady) onReady()
        trackBuyAngleData({
          source: buyAngleSourceLabel(),
          device_type: deviceTypeFromViewport(),
          links_clicked: 0,
          dot_clicked: false,
          clicked_filters: false
        })
      } catch (_) {
        if (!cancelled) {
          setInitError(true)
          setLoading(false)
          if (onReady) onReady()
        }
      }
    }
    run()
    return () => { cancelled = true }
  }, [onReady, trackBuyAngleData])

  const svgPointFromClient = useCallback((clientX, clientY) => {
    const svg = svgRef.current
    if (!svg) return centroid
    const rect = svg.getBoundingClientRect()
    const x = VB.x + (clientX - rect.left) * (VB.width / rect.width)
    const y = VB.y + (clientY - rect.top) * (VB.height / rect.height)
    return { x, y }
  }, [])

  const updateChartCursor = useCallback((clientX, clientY) => {
    const stack = stackRef.current
    if (!stack || clientX == null || clientY == null) return
    const handle = handleRef.current
    let overHandle = false
    if (handle) {
      const r = handle.getBoundingClientRect()
      overHandle =
        clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom
    }
    const pt = svgPointFromClient(clientX, clientY)
    const inTri = inside(pt, A, B, C)
    stack.style.cursor = overHandle || inTri ? 'grab' : 'default'
  }, [svgPointFromClient])

  const moveTo = useCallback((p) => {
    const clamped = clampToTri(p)
    setHandlePoint(snapToCornerIfNear(clamped))
  }, [])

  const onHandlePointerDown = useCallback((e) => {
    // Visual handle can extend outside the triangle; cursor may be outside the fill but on the dot.
    // Start drag without moving the dot (square viewBox must not reparent the point).
    e.stopPropagation()
    draggingRef.current = true
    setDragging(true)
    if (stackRef.current) stackRef.current.style.cursor = 'grabbing'
    const target = e.currentTarget
    try {
      if (target && typeof target.setPointerCapture === 'function') {
        target.setPointerCapture(e.pointerId)
      }
    } catch (_) {}
  }, [])

  const onPointerDown = useCallback((e) => {
    if (handleRef.current && handleRef.current.contains(e.target)) {
      return
    }
    const pt = e.touches && e.touches.length
      ? svgPointFromClient(e.touches[0].clientX, e.touches[0].clientY)
      : svgPointFromClient(e.clientX, e.clientY)
    if (!inside(pt, A, B, C)) {
      return
    }
    draggingRef.current = true
    setDragging(true)
    if (stackRef.current) stackRef.current.style.cursor = 'grabbing'
    try { if (svgRef.current) svgRef.current.setPointerCapture(e.pointerId) } catch (_) {}
    moveTo(pt)
  }, [svgPointFromClient, moveTo])

  const onStackPointerMove = useCallback(
    (e) => {
      if (draggingRef.current) {
        if (stackRef.current) stackRef.current.style.cursor = 'grabbing'
        return
      }
      updateChartCursor(e.clientX, e.clientY)
    },
    [updateChartCursor]
  )

  const onStackPointerLeave = useCallback(() => {
    if (!draggingRef.current && stackRef.current) {
      stackRef.current.style.cursor = 'default'
    }
  }, [])

  useEffect(() => {
    const onMove = (e) => {
      if (!draggingRef.current) return
      // Stop the page from scrolling on touch while dragging the handle (especially vertical).
      if (e.type === 'touchmove' && e.cancelable) {
        e.preventDefault()
      } else if (e.type === 'pointermove' && e.pointerType === 'touch' && e.cancelable) {
        e.preventDefault()
      }
      const clientX = (e.touches && e.touches[0] ? e.touches[0].clientX : e.clientX)
      const clientY = (e.touches && e.touches[0] ? e.touches[0].clientY : e.clientY)
      if (clientX == null || clientY == null) return
      const pt = svgPointFromClient(clientX, clientY)
      moveTo(pt)
      if (stackRef.current) stackRef.current.style.cursor = 'grabbing'
    }
    const onUp = (e) => {
      const wasDragging = draggingRef.current
      if (!wasDragging) {
        return
      }
      draggingRef.current = false
      setDragging(false)
      trackBuyAngleData({ dot_clicked: true })
      try { if (svgRef.current) svgRef.current.releasePointerCapture && svgRef.current.releasePointerCapture() } catch (_) {}
      try {
        if (handleRef.current && typeof e.pointerId === 'number') {
          handleRef.current.releasePointerCapture(e.pointerId)
        }
      } catch (_) {}
      let cx = e.clientX
      let cy = e.clientY
      if ((cx == null || cy == null) && e.changedTouches && e.changedTouches[0]) {
        cx = e.changedTouches[0].clientX
        cy = e.changedTouches[0].clientY
      }
      if (cx != null && cy != null) {
        updateChartCursor(cx, cy)
      } else if (stackRef.current) {
        stackRef.current.style.cursor = 'default'
      }
    }
    document.addEventListener('pointermove', onMove, { passive: false })
    document.addEventListener('pointerup', onUp)
    document.addEventListener('pointercancel', onUp)
    document.addEventListener('touchmove', onMove, { passive: false })
    document.addEventListener('touchend', onUp, { passive: true })
    return () => {
      document.removeEventListener('pointermove', onMove)
      document.removeEventListener('pointerup', onUp)
      document.removeEventListener('pointercancel', onUp)
      document.removeEventListener('touchmove', onMove)
      document.removeEventListener('touchend', onUp)
    }
  }, [moveTo, svgPointFromClient, updateChartCursor, trackBuyAngleData])

  const onKeyDown = useCallback((e) => {
    const step = e.shiftKey ? 8 : 3
    let d = { x: 0, y: 0 }
    if (e.key === 'ArrowUp') d = { x: 0, y: -step }
    else if (e.key === 'ArrowDown') d = { x: 0, y: step }
    else if (e.key === 'ArrowLeft') d = { x: -step, y: 0 }
    else if (e.key === 'ArrowRight') d = { x: step, y: 0 }
    else return
    e.preventDefault()
    moveTo(add(handlePoint, d))
  }, [handlePoint, moveTo])

  const w = weightsFromPoint(handlePoint)
  // Meters: literal barycentric % (corner = 100% for that metric; centroid ≈ 33% each).
  const barFillPct = (key) => Math.min(100, Math.max(0, w[key] * 100))
  const colorInt = (key) => Math.min(1, w[key])
  const DEFAULT_FILL = { r: 21, g: 34, b: 65 }
  const PRICE_RGB = { r: 224, g: 122, b: 122 }
  const SIZE_RGB = { r: 96, g: 165, b: 250 }
  const PERF_RGB = { r: 232, g: 200, b: 74 }
  const ci = { price: colorInt('price'), size: colorInt('size'), perf: colorInt('perf') }
  // Linear color blend: each metric contributes exactly by its displayed percentage.
  const wp = ci.price
  const ws = ci.size
  const wf = ci.perf
  const total = wp + ws + wf
  const triangleFill = total > 0
    ? `rgb(${Math.round((wp * PRICE_RGB.r + ws * SIZE_RGB.r + wf * PERF_RGB.r) / total)}, ${Math.round((wp * PRICE_RGB.g + ws * SIZE_RGB.g + wf * PERF_RGB.g) / total)}, ${Math.round((wp * PRICE_RGB.b + ws * SIZE_RGB.b + wf * PERF_RGB.b) / total)})`
    : `rgb(${DEFAULT_FILL.r}, ${DEFAULT_FILL.g}, ${DEFAULT_FILL.b})`
  // Scoring uses the full catalog for price/size/perf scale (pill filters only exclude from the list).
  const rawMinP = tvs.length ? Math.min(...tvs.map(t => t.exact_price)) : 0
  const rawMaxP = tvs.length ? Math.max(...tvs.map(t => t.exact_price)) : 1
  let priceNormMax = rawMaxP > PRICE_SCORE_CAP_DOLLARS ? PRICE_SCORE_CAP_DOLLARS : rawMaxP
  let priceNormMin = rawMinP
  if (priceNormMax <= priceNormMin) {
    priceNormMin = rawMinP
    priceNormMax = rawMaxP
  }
  const rawMinS = tvs.length ? Math.min(...tvs.map(t => t.size)) : 0
  const rawMaxS = tvs.length ? Math.max(...tvs.map(t => t.size)) : 1
  let sizeNormMax = rawMaxS
  let sizeNormMin = Math.min(rawMinS, TV_SIZE_NORM_FLOOR_INCHES)
  if (sizeNormMax <= sizeNormMin) {
    sizeNormMin = rawMinS
    sizeNormMax = rawMaxS
  }
  const perfValues = tvs.map(t => performanceRatingFor(t, optimizeFor))
  const maxPerformanceRating = perfValues.length ? Math.max(...perfValues) : 0

  const userPriceWeight = w.price
  const userSizeWeight = w.size
  const userPerformanceWeight = w.perf
  const matchScoreDisplayCap = Math.max(
    userPriceWeight * 100 +
      userSizeWeight * 100 +
      userPerformanceWeight * 100 +
      MATCH_SCORE_DISPLAY_BONUS_HEADROOM,
    1
  )

  const scoreCtx = {
    userPriceWeight,
    userSizeWeight,
    userPerformanceWeight,
    priceNormMin,
    priceNormMax,
    sizeNormMin,
    sizeNormMax,
    maxPerformanceRating,
    optimizeFor
  }

  const sorted = filteredTvs.length
    ? [...filteredTvs]
      .map(tv => ({ tv, score: triangleTvFinalScore(tv, scoreCtx) }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 8)
    : []
  const resultsMotionTransition = reduceMotion
    ? { duration: 0.01 }
    : {
        layout: { duration: 0.68, ease: TV_RESULTS_LAYOUT_EASE },
        opacity: { duration: 0.42, ease: 'easeOut' }
      }

  if (loading) {
    return (
      <div className="tv-triangle-picker tv-triangle-loading">
        <div className="spinner-container">
          <div className="lds-roller"><div></div><div></div><div></div><div></div><div></div><div></div><div></div><div></div></div>
        </div>
      </div>
    )
  }

  if (initError) {
    return (
      <div className="tv-triangle-picker tv-triangle-loading">
        <p className="tv-triangle-init-error">Could not start BuyAngle. Please refresh the page.</p>
      </div>
    )
  }

  return (
    <div className="tv-triangle-picker">
      <header className="tv-triangle-header">
        <div className="tv-triangle-title">TV BuyAngle</div>
        <div className="tv-triangle-subtitle">Drag the dot to find your desired mix of Performance, Size and Affordablilty. Pills can filter results.</div>
      </header>
      <main className="tv-triangle-layout">
        <section className="tv-triangle-panel tv-triangle-panel-left">
          <div className="tv-triangle-panel-optimize">
            <label className="tv-triangle-panel-optimize-label" htmlFor="tv-triangle-optimize-for">Optimize for</label>
            <select
              id="tv-triangle-optimize-for"
              className="tv-triangle-optimize-select"
              value={optimizeFor}
              onChange={(e) => setOptimizeFor(e.target.value)}
            >
              {OPTIMIZE_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div className="tv-triangle-mosaic">
            <div className="tv-triangle-wrap">
              <div
                ref={stackRef}
                className="tv-triangle-svg-stack"
                onPointerMove={onStackPointerMove}
                onPointerLeave={onStackPointerLeave}
              >
              <svg
                ref={svgRef}
                className="tv-triangle-svg"
                viewBox={`0 0 ${VB.width} ${VB.height}`}
                overflow="visible"
                aria-label="Buying Triangle"
                role="application"
                tabIndex={0}
                onKeyDown={onKeyDown}
                style={{ overflow: 'visible' }}
              >
                <g>
                  <polygon
                    className="tv-triangle-triangle-surface"
                    points={`${A.x},${A.y} ${B.x},${B.y} ${C.x},${C.y}`}
                    fill={triangleFill}
                    stroke="rgba(255,255,255,.6)"
                    strokeWidth="2.5"
                    onPointerDown={onPointerDown}
                  />
                  <text
                    className="tv-triangle-vertex-label"
                    x={labelPrice.x}
                    y={labelPrice.y}
                    fill="var(--tv-price)"
                    textAnchor="end"
                    dominantBaseline="middle"
                    pointerEvents="none"
                  >Affordable</text>
                  <text
                    className="tv-triangle-vertex-label"
                    x={labelSize.x}
                    y={labelSize.y}
                    fill="var(--tv-size)"
                    textAnchor="start"
                    dominantBaseline="middle"
                    pointerEvents="none"
                  >Size</text>
                  <text
                    className="tv-triangle-vertex-label"
                    x={LABEL_PERF.x}
                    y={LABEL_PERF.y}
                    fill="var(--tv-perf)"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    pointerEvents="none"
                  >Performance</text>
                </g>
              </svg>
              <div
                ref={handleRef}
                className="tv-triangle-handle-fixed"
                style={{
                  left: `${(handlePoint.x / VB.width) * 100}%`,
                  top: `${(handlePoint.y / VB.height) * 100}%`
                }}
                onPointerDown={onHandlePointerDown}
                onTouchStart={(e) => { e.stopPropagation() }}
                aria-hidden
              >
                <span className="tv-triangle-handle-fixed-pulse" />
              </div>
            </div>
            </div>
            <div className="tv-triangle-weights">
            <div className="tv-triangle-weight">
              <div className="tv-triangle-weight-label tv-triangle-weight-label--price">Affordable</div>
              <div className="tv-triangle-weight-value">{Math.round(barFillPct('price'))}%</div>
              <div className="tv-triangle-bar"><span className="tv-triangle-bar-fill tv-triangle-bar-price" style={{ width: barFillPct('price') + '%' }} /></div>
            </div>
            <div className="tv-triangle-weight">
              <div className="tv-triangle-weight-label tv-triangle-weight-label--perf">Performance</div>
              <div className="tv-triangle-weight-value">{Math.round(barFillPct('perf'))}%</div>
              <div className="tv-triangle-bar"><span className="tv-triangle-bar-fill tv-triangle-bar-perf" style={{ width: barFillPct('perf') + '%' }} /></div>
            </div>
            <div className="tv-triangle-weight">
              <div className="tv-triangle-weight-label tv-triangle-weight-label--size">Size</div>
              <div className="tv-triangle-weight-value">{Math.round(barFillPct('size'))}%</div>
              <div className="tv-triangle-bar"><span className="tv-triangle-bar-fill tv-triangle-bar-size" style={{ width: barFillPct('size') + '%' }} /></div>
            </div>
            </div>
          </div>
        </section>
        <section className="tv-triangle-panel tv-triangle-panel-right">
          <div className="tv-triangle-panel-title-row">
            {pillFilters.length === 0 && <h2 className="tv-triangle-panel-title">Recommended TVs</h2>}
            {pillFilters.length > 0 && (
              <div className="tv-triangle-filter-bar">
                <div className="tv-triangle-filter-pills" role="list">
                  {pillFilters.map((f) => (
                    <button
                      key={filterPillReactKey(f)}
                      type="button"
                      role="listitem"
                      className={filterBarPillClassName()}
                      onClick={() => removePillFromBar(f)}
                      aria-label={'Remove filter: ' + formatPillFilterOne(f)}
                    >
                      {formatPillFilterOne(f)}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  className="tv-triangle-filter-clear"
                  onClick={() => {
                    trackBuyAngleDataFiltersUsed()
                    setPillFilters([])
                  }}
                >
                  Clear filters
                </button>
              </div>
            )}
          </div>
          <div className="tv-triangle-results" aria-live="polite">
            <AnimatePresence initial={false} mode="popLayout">
              {sorted.length === 0 && pillFilters.length > 0 && (
                <div key="tv-triangle-no-results" className="tv-triangle-no-results">
                  <span className="tv-triangle-no-results-text">No TVs match your filters.</span>
                  <button
                    type="button"
                    className="tv-triangle-filter-clear"
                    onClick={() => {
                      trackBuyAngleDataFiltersUsed()
                      setPillFilters([])
                    }}
                  >
                    Clear filters
                  </button>
                </div>
              )}
              {sorted.map((item, idx) => {
                const { tv, score: s } = item
                const sizeInchesPill = tvSizeInchesForPill(tv)
                const metricScores = metricScoresForTv(tv, scoreCtx)
                const perf = Math.round(metricScores.normalizedPerformanceScore)
                const sizeMeterPct = fixedSizeMeterPct(tv.size || 0)
                const priceMeterPct = Math.round(Math.min(100, Math.max(0, metricScores.normalizedPriceScore)))
                const perfMeterPct = fixedPerformanceMeterPct(metricScores.normalizedPerformanceScore)
                const displayOutOf100 = Math.round(
                  Math.min(100, Math.max(0, (s / matchScoreDisplayCap) * 100))
                )
                const cardKey = `${tv.id}-${tv.size}`
                return (
                  <motion.div
                    key={cardKey}
                    layout
                    className={'tv-triangle-card' + (idx === 0 ? ' tv-triangle-card--top-pick' : '')}
                    transition={resultsMotionTransition}
                    initial={reduceMotion ? false : { opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={reduceMotion
                      ? { opacity: 0 }
                      : {
                          opacity: 0,
                          y: 120,
                          transition: { duration: 0.62, ease: TV_RESULTS_EXIT_EASE }
                        }}
                  >
                    <a
                      className="tv-triangle-card-hit"
                      href={tv.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        trackBuyAngleClick({
                          dot_x: Number(handlePoint.x.toFixed(2)),
                          dot_y: Number(handlePoint.y.toFixed(2)),
                          optimize_for: optimizeFor,
                          filters_selected: pillFilters,
                          result_engaged_with: idx + 1,
                          product_clicked_id: tv.id,
                          source: buyAngleSourceLabel(),
                          device_type: deviceTypeFromViewport()
                        })
                      }}
                      aria-label={`View ${[tv.brand, tv.name].filter(Boolean).join(' ').trim() || 'product'}`}
                    />
                    <div className="tv-triangle-card-surface">
                      <div className="tv-triangle-card-head">
                        <div className="tv-triangle-card-thumb-wrap" aria-hidden>
                          {tv.image_url
                            ? (
                              <Image
                                className="tv-triangle-card-thumb"
                                src={tv.image_url}
                                alt=""
                                fill
                                sizes="76px"
                                loading="lazy"
                              />
                              )
                            : (
                              <div className="tv-triangle-card-thumb tv-triangle-card-thumb--placeholder" />
                              )}
                        </div>
                        <div className="tv-triangle-card-title">
                          <span className="tv-triangle-card-title-text">{tv.name}</span>
                          <div className="tv-triangle-badges" role="group" aria-label="Filter by attribute">
                            {tv.brand && (
                              <button
                                type="button"
                                className={'tv-triangle-pill' + (pillIsActiveInList(pillFilters, { key: 'brand', value: tv.brand }) ? ' tv-triangle-pill--active' : '')}
                                onClick={(e) => onPillClick(e, { key: 'brand', value: tv.brand })}
                                aria-pressed={pillIsActiveInList(pillFilters, { key: 'brand', value: tv.brand })}
                              >
                                {tv.brand}
                              </button>
                            )}
                            {sizeInchesPill != null && (
                              <button
                                type="button"
                                className={'tv-triangle-pill' + (pillIsActiveInList(pillFilters, { key: 'size', value: sizeInchesPill }) ? ' tv-triangle-pill--active' : '')}
                                onClick={(e) => onPillClick(e, { key: 'size', value: sizeInchesPill })}
                                aria-pressed={pillIsActiveInList(pillFilters, { key: 'size', value: sizeInchesPill })}
                              >
                                {sizeInchesPill}&quot;
                              </button>
                            )}
                            {tv.pixel_type && (
                              <button
                                type="button"
                                className={'tv-triangle-pill' + (pillIsActiveInList(pillFilters, { key: 'pixel_type', value: tv.pixel_type }) ? ' tv-triangle-pill--active' : '')}
                                onClick={(e) => onPillClick(e, { key: 'pixel_type', value: tv.pixel_type })}
                                aria-pressed={pillIsActiveInList(pillFilters, { key: 'pixel_type', value: tv.pixel_type })}
                              >
                                {tv.pixel_type}
                              </button>
                            )}
                            {tv.resolution === '8K' && (
                              <button
                                type="button"
                                className={'tv-triangle-pill' + (pillIsActiveInList(pillFilters, { key: 'resolution', value: '8K' }) ? ' tv-triangle-pill--active' : '')}
                                onClick={(e) => onPillClick(e, { key: 'resolution', value: '8K' })}
                                aria-pressed={pillIsActiveInList(pillFilters, { key: 'resolution', value: '8K' })}
                              >
                                8K
                              </button>
                            )}
                            {tv.room_brightness_text === 'very_bright' && (
                              <button
                                type="button"
                                className={'tv-triangle-pill' + (pillIsActiveInList(pillFilters, { key: 'very_bright', value: true }) ? ' tv-triangle-pill--active' : '')}
                                title="Approved for bright rooms"
                                onClick={(e) => onPillClick(e, { key: 'very_bright', value: true })}
                                aria-pressed={pillIsActiveInList(pillFilters, { key: 'very_bright', value: true })}
                              >
                                Bright Rooms <span className="tv-triangle-pill-check">✓</span>
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="tv-triangle-score-wrap" aria-label="Match score for this pick">
                          <div className="tv-triangle-score">{displayOutOf100}</div>
                          <div className="tv-triangle-score-label">Score</div>
                        </div>
                      </div>
                      <div className="tv-triangle-card-metrics" role="group" aria-label="Affordable, performance, and size">
                        <div className="tv-triangle-metric tv-triangle-metric-price">
                          <span className="tv-triangle-metric-label">Affordable</span>
                          <span className="tv-triangle-metric-value">${(tv.exact_price || 0).toLocaleString()}</span>
                          <div className="tv-triangle-metric-meter" aria-label={`Affordable meter ${priceMeterPct} out of 100`}>
                            <span className="tv-triangle-metric-meter-fill tv-triangle-metric-meter-fill--price" style={{ width: `${priceMeterPct}%` }} />
                          </div>
                        </div>
                        <div className="tv-triangle-metric tv-triangle-metric-perf">
                          <span className="tv-triangle-metric-label">Performance</span>
                          <span className="tv-triangle-metric-value">{perf}<span className="tv-triangle-metric-suffix">/100</span></span>
                          <div className="tv-triangle-metric-meter" aria-label={`Performance meter ${perfMeterPct} out of 100`}>
                            <span className="tv-triangle-metric-meter-fill tv-triangle-metric-meter-fill--perf" style={{ width: `${perfMeterPct}%` }} />
                          </div>
                        </div>
                        <div className="tv-triangle-metric tv-triangle-metric-size">
                          <span className="tv-triangle-metric-label">Size</span>
                          <span className="tv-triangle-metric-value">{tv.size}"</span>
                          <div className="tv-triangle-metric-meter" aria-label={`Size meter ${sizeMeterPct} out of 100`}>
                            <span className="tv-triangle-metric-meter-fill tv-triangle-metric-meter-fill--size" style={{ width: `${sizeMeterPct}%` }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )
              })}
            </AnimatePresence>
          </div>
        </section>
      </main>
    </div>
  )
}

TvTrianglePicker.propTypes = {
  onReady: PropTypes.func
}
